import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  CancelarItemPedidoDTO,
  ItemDePedidoDTO,
  LoteDeItensDePedidoDTO,
  PedidoConsultaDTO,
  PedidoDTO,
  PedidoParaFaturarFiltroDTO,
  PedidoParaFaturarResponseDTO,
  PedidoResponseDTO,
} from '../../models/pedidos/pedido.model';

@Injectable({ providedIn: 'root' })
export class PedidoService implements ApiInterface {

  constructor(private apiService: ApiService) {}

  get<T>(url: string, data?: any): Observable<PedidoResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<PedidoResponseDTO>(path);
  }

  /**
   * Pedidos com saldo a faturar, para o popup de importacao na tela de venda.
   *
   * Endpoint proprio, e nao o `/pedidos`: o filtro "tem algo a faturar" e regra do caso de uso
   * faturar, e a resposta vem sem itens — o que evita dezenas de queries por pagina no backend.
   */
  listarParaFaturar(filtro: PedidoParaFaturarFiltroDTO): Observable<PedidoParaFaturarResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(filtro);
    const path = queryParams ? `/pedidos/para-faturar?${queryParams}` : '/pedidos/para-faturar';
    return this.apiService.read<PedidoParaFaturarResponseDTO>(path);
  }

  post<T>(url: string, data: any): Observable<any> {
    return this.apiService.create<any>(url, data);
  }

  put<T>(url: string, data: any): Observable<any> {
    return this.apiService.update<any>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // Metodos especificos de dominio

  getById(id: number): Observable<PedidoDTO> {
    return this.apiService.read<PedidoDTO>(`/pedidos/${id}`);
  }

  enviarParaProducao(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/enviar-para-producao/${id}`, {});
  }

  enviarACaminho(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/a-caminho/${id}`, {});
  }

  enviarAguardandoRetirada(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/aguardando-retirada/${id}`, {});
  }

  cancelarPedido(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/cancelar/${id}`, {});
  }

  cancelarItem(dto: CancelarItemPedidoDTO): Observable<any> {
    return this.apiService.patch(`/pedidos/cancelarItem`, dto);
  }

  finalizarPedido(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/finalizar/${id}`, {});
  }

  /**
   * Adiciona (ou incrementa, via MERGE no backend) um item a um pedido
   * existente. Retorna o pedido atualizado (PedidoConsultaDTO).
   */
  adicionarItem(pedidoId: number, dto: ItemDePedidoDTO): Observable<PedidoConsultaDTO> {
    return this.apiService.create<PedidoConsultaDTO>(`/pedidos/${pedidoId}/item`, dto);
  }

  /**
   * Aplica de uma vez todas as alteracoes de itens montadas na tela: itens
   * novos e novas quantidades de linhas existentes. Transacional (tudo ou
   * nada) e gera UMA comanda complementar por impressora — ao contrario do
   * POST avulso, em que cada item imprimia sozinho.
   */
  alterarItensEmLote(pedidoId: number, lote: LoteDeItensDePedidoDTO): Observable<PedidoConsultaDTO> {
    return this.apiService.create<PedidoConsultaDTO>(`/pedidos/${pedidoId}/itens/lote`, lote);
  }

  /**
   * Altera a quantidade (do pedido) de um item existente. O backend recalcula
   * saldo, valorTotal e os totais do pedido. Retorna o pedido atualizado.
   */
  alterarQuantidadeItem(itemDePedidoId: number, quantidade: number): Observable<PedidoConsultaDTO> {
    return this.apiService.patch(
      `/pedidos/item/${itemDePedidoId}/quantidade?quantidade=${quantidade}`,
      {},
    );
  }

  entregar(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/entregar/${id}`, {});
  }

  entregarEFinalizar(id: number): Observable<any> {
    return this.apiService.patch(`/pedidos/entregar-e-finalizar/${id}`, {});
  }
}
